
import React, {
  useState,
  useRef,
} from "react"

import PulseLoader
  from "react-spinners/PulseLoader"

import {
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"

import {
  useGetOrgIntentionResponseSummaryQuery,
  useLazyGetOrgIntentionResponseByCandidateQuery,
} from "@/features/orgIntentionBoards/orgIntentionResponsesApiSlice"

// 13 Sep 2026
import EtOrgIntentionResponseInsights
  from "@/features/orgIntentionBoards/EtOrgIntentionResponseInsights"


const EtOrgIntentionResponseBoard = ({
  intentionBoardId,
}) => {

  // First Created - 07 Sep 2026
  // Author - Atul Pandey
  // Purpose - Internal Intention Board response overview and
  //           submitted candidate response viewer.
  //
  // Updated - 07 Sep 2026 - Added Submission % donut chart.
  // Updated - 10 Sep 2026 - Summary card and candidate sorting refinements.
  // Updated - 13 Sep 2026 - Response Insights.
  // Updated - 14 Sep 2026 - Independent candidate/detail scrolling.
  // Updated - 29 Sep 2026 - Candidate search by name or email.


  /*
  |--------------------------------------------------------------------------
  | 01 - Selected Candidate
  |--------------------------------------------------------------------------
  */

  const [
    selectedCandidateId,
    setSelectedCandidateId,
  ] = useState(null)


  const [
    responseView,
    setResponseView,
  ] = useState("responses")


  /*
  |--------------------------------------------------------------------------
  | Candidate Search - 29 Sep 2026
  |--------------------------------------------------------------------------
  */

  const [
    candidateSearch,
    setCandidateSearch,
  ] = useState("")


  /*
  |--------------------------------------------------------------------------
  | Response Detail Pane Ref
  |--------------------------------------------------------------------------
  */

  const responseDetailPaneRef =
    useRef(null)


  /*
  |--------------------------------------------------------------------------
  | 02 - Response Summary
  |--------------------------------------------------------------------------
  */

  const {

    data:
      responseSummaryData,

    isLoading:
      isLoadingSummary,

    isFetching:
      isFetchingSummary,

    isError:
      isSummaryError,

    error:
      summaryError,

  } =
    useGetOrgIntentionResponseSummaryQuery(
      {
        intentionBoardId,
      },
      {
        skip:
          !intentionBoardId,
      }
    )


  /*
  |--------------------------------------------------------------------------
  | 03 - Candidate Response Detail
  |--------------------------------------------------------------------------
  */

  const [

    getOrgIntentionResponseByCandidate,

    {

      data:
        candidateResponseData,

      isFetching:
        isFetchingCandidateResponse,

      isError:
        isCandidateResponseError,

      error:
        candidateResponseError,

    },

  ] =
    useLazyGetOrgIntentionResponseByCandidateQuery()


  /*
  |--------------------------------------------------------------------------
  | 04 - Derived Data
  |--------------------------------------------------------------------------
  */

  const board =
    responseSummaryData?.board || null


  const summary =
    responseSummaryData?.summary || {}


  const candidates =
    responseSummaryData?.candidates || []


  const selectedCandidate =
    candidateResponseData?.candidate || null


  const selectedResponse =
    candidateResponseData?.response || null


  const responseQuestions =
    candidateResponseData?.questions || []


  /*
  |--------------------------------------------------------------------------
  | Sort Candidates - 10 Sep 2026
  |--------------------------------------------------------------------------
  |
  | Order:
  |
  | 1. Submitted - Earliest submission first
  | 2. In Progress - Earliest access first
  | 3. Accessed - Earliest access first
  | 4. Nominated - Alphabetical
  | 5. Remaining statuses - Alphabetical
  |
  */

  const sortedCandidates =
    (candidates || [])
      .slice()
      .sort(
        (a, b) => {

          const getCandidateRank =
            candidate => {

              if (
                candidate?.hasSubmittedResponse === true
              ) {
                return 1
              }

              if (
                candidate?.candidateStatus === "draft"
              ) {
                return 2
              }

              if (
                candidate?.candidateStatus === "accessed"
              ) {
                return 3
              }

              if (
                candidate?.candidateStatus === "nominated"
              ) {
                return 4
              }

              return 5

            }


          const aRank =
            getCandidateRank(a)


          const bRank =
            getCandidateRank(b)


          if (
            aRank !== bRank
          ) {
            return aRank - bRank
          }


          /*
          |--------------------------------------------------------------------------
          | Submitted - Earliest Submission First
          |--------------------------------------------------------------------------
          */

          if (
            aRank === 1
          ) {

            const aSubmittedAt =
              a?.submittedAt
                ? new Date(a.submittedAt).getTime()
                : Number.MAX_SAFE_INTEGER


            const bSubmittedAt =
              b?.submittedAt
                ? new Date(b.submittedAt).getTime()
                : Number.MAX_SAFE_INTEGER


            if (
              aSubmittedAt !== bSubmittedAt
            ) {
              return aSubmittedAt - bSubmittedAt
            }

          }


          /*
          |--------------------------------------------------------------------------
          | In Progress / Accessed - Earliest Access First
          |--------------------------------------------------------------------------
          */

          if (
            aRank === 2 ||
            aRank === 3
          ) {

            const aAccessedAt =
              a?.firstAccessedAt
                ? new Date(a.firstAccessedAt).getTime()
                : Number.MAX_SAFE_INTEGER


            const bAccessedAt =
              b?.firstAccessedAt
                ? new Date(b.firstAccessedAt).getTime()
                : Number.MAX_SAFE_INTEGER


            if (
              aAccessedAt !== bAccessedAt
            ) {
              return aAccessedAt - bAccessedAt
            }

          }


          return (
            a?.candidateName || ""
          ).localeCompare(
            b?.candidateName || ""
          )

        }
      )


  /*
  |--------------------------------------------------------------------------
  | Filter Candidates - 29 Sep 2026
  |--------------------------------------------------------------------------
  |
  | Search is applied AFTER sorting.
  |
  | Matching:
  | - Candidate name
  | - Candidate email
  | - Case insensitive
  | - Trims leading/trailing spaces
  |
  | No change to the selected response.
  |
  */

  const normalizedCandidateSearch =
    candidateSearch
      .trim()
      .toLowerCase()


  const filteredCandidates =
    normalizedCandidateSearch
      ?
      sortedCandidates.filter(
        candidate => {

          const candidateName =
            String(
              candidate?.candidateName || ""
            )
              .toLowerCase()


          const candidateEmail =
            String(
              candidate?.candidateEmail || ""
            )
              .toLowerCase()


          return (
            candidateName.includes(
              normalizedCandidateSearch
            )
            ||
            candidateEmail.includes(
              normalizedCandidateSearch
            )
          )

        }
      )
      :
      sortedCandidates


  /*
  |--------------------------------------------------------------------------
  | 05 - Submission Percentage
  |--------------------------------------------------------------------------
  */

  const totalCandidates =
    Number(
      summary?.totalCandidates || 0
    )


  const submittedCandidates =
    Number(
      summary?.submitted || 0
    )


  const submissionPercentage =
    totalCandidates > 0
      ?
      Math.round(
        (
          submittedCandidates /
          totalCandidates
        ) * 100
      )
      :
      0


  /*
  |--------------------------------------------------------------------------
  | 06 - Donut Chart Geometry
  |--------------------------------------------------------------------------
  */

  const donutRadius =
    36


  const donutCircumference =
    2 * Math.PI * donutRadius


  const donutProgress =
    (
      submissionPercentage / 100
    ) * donutCircumference


  const donutRemaining =
    donutCircumference - donutProgress


  /*
  |--------------------------------------------------------------------------
  | 07 - Format Date
  |--------------------------------------------------------------------------
  */

  const formatDateTime =
    value => {

      if (!value) {
        return ""
      }


      const date =
        new Date(value)


      if (
        Number.isNaN(
          date.getTime()
        )
      ) {
        return ""
      }


      return date.toLocaleString(
        undefined,
        {
          dateStyle: "medium",
          timeStyle: "short",
        }
      )

    }


  /*
  |--------------------------------------------------------------------------
  | 08 - Candidate Status Label
  |--------------------------------------------------------------------------
  */

  const getCandidateStatusLabel =
    candidate => {

      if (
        candidate?.isOverdue
      ) {
        return "Overdue"
      }


      switch (
        candidate?.candidateStatus
      ) {

        case "nominated":
          return "Nominated"

        case "invited":
          return "Invited"

        case "accessed":
          return "Accessed"

        case "draft":
          return "In Progress"

        case "submitted":
          return "Submitted"

        case "expired":
          return "Expired"

        case "withdrawn":
          return "Withdrawn"

        default:
          return "Unknown"

      }

    }


  /*
  |--------------------------------------------------------------------------
  | 09 - Candidate Status Class
  |--------------------------------------------------------------------------
  */

  const getCandidateStatusClass =
    candidate => {

      if (
        candidate?.isOverdue
      ) {
        return "bg-orange-100 text-orange-700"
      }


      switch (
        candidate?.candidateStatus
      ) {

        case "submitted":
          return "bg-lime-100 text-lime-700"

        case "draft":
        case "accessed":
          return "bg-yellow-100 text-yellow-700"

        case "withdrawn":
        case "expired":
          return "bg-gray-200 text-gray-600"

        default:
          return "bg-gray-100 text-gray-600"

      }

    }


  /*
  |--------------------------------------------------------------------------
  | 10 - Select Candidate
  |--------------------------------------------------------------------------
  */

  const handleSelectCandidate =
    async candidate => {

      if (
        !candidate?.hasSubmittedResponse
      ) {
        return
      }


      setSelectedCandidateId(
        candidate.intentionCandidateId
      )


      responseDetailPaneRef.current?.scrollTo({
        top: 0,
        behavior: "smooth",
      })


      try {

        await getOrgIntentionResponseByCandidate({

          intentionBoardId,

          intentionCandidateId:
            candidate.intentionCandidateId,

        }).unwrap()

      } catch (error) {

        console.error(error)

      }

    }


  /*
  |--------------------------------------------------------------------------
  | 11 - Close Candidate Detail
  |--------------------------------------------------------------------------
  */

  const handleCloseCandidate =
    () => {

      setSelectedCandidateId(null)

    }


  /*
  |--------------------------------------------------------------------------
  | 12 - Render Answer
  |--------------------------------------------------------------------------
  */

  const renderAnswer =
    question => {

      /*
      |--------------------------------------------------------------------------
      | Text Responses
      |--------------------------------------------------------------------------
      */

      if (
        [
          "reflection",
          "short_text",
          "long_text",
        ].includes(
          question?.questionType
        )
      ) {

        return (

          <p className="text-xs text-gray-700 whitespace-pre-wrap leading-5">

            {
              question?.responseText ||
              "No response"
            }

          </p>

        )

      }


      /*
      |--------------------------------------------------------------------------
      | Single / Multi Select
      |--------------------------------------------------------------------------
      */

      if (
        [
          "single_select",
          "multi_select",
        ].includes(
          question?.questionType
        )
      ) {

        const selectedOptions =
          question?.selectedOptionDetails || []


        if (
          selectedOptions.length === 0
        ) {

          return (

            <p className="text-xs text-gray-500">
              No response
            </p>

          )

        }


        return (

          <div className="flex justify-start items-center flex-wrap">

            {selectedOptions.map(
              (option, index) => (

                <div

                  key={
                    `${option?.optionValue}-${index}`
                  }

                  className="bg-orange-50 rounded px-3 py-2 mr-2 mb-2"

                >

                  <p className="text-xs font-bold text-gray-700">

                    {
                      option?.optionLabel ||
                      option?.optionValue
                    }

                  </p>

                </div>

              )
            )}

          </div>

        )

      }


      /*
      |--------------------------------------------------------------------------
      | Confirmation
      |--------------------------------------------------------------------------
      */

      if (
        question?.questionType ===
        "checkbox_confirmation"
      ) {

        return (

          <p className="text-xs font-bold text-gray-700">

            {
              question?.confirmationValue
                ? "Confirmed"
                : "Not Confirmed"
            }

          </p>

        )

      }


      return (

        <p className="text-xs text-gray-500">
          No response
        </p>

      )

    }


  /*
  |--------------------------------------------------------------------------
  | 13 - Loading
  |--------------------------------------------------------------------------
  */

  if (
    isLoadingSummary
  ) {

    return (

      <div className="bg-white rounded m-1 p-4">

        <PulseLoader
          color={"#ea580c"}
          size={7}
        />

      </div>

    )

  }


  /*
  |--------------------------------------------------------------------------
  | 14 - Render
  |--------------------------------------------------------------------------
  */

  return (

    <div className="m-1 p-1 bg-white rounded">

      {/* Header */}

      <div className="m-1 p-2">

        <p className="font-bold text-lg text-gray-800">
          Intention Board Responses
        </p>

        <p className="text-xs text-gray-500 mt-1">

          {
            board?.boardName ||
            "Intention Board"
          }

        </p>

      </div>


      {/*
      |--------------------------------------------------------------------------
      | Response View Selector
      |--------------------------------------------------------------------------
      */}

      <div className="bg-gray-100 rounded m-1 p-1">

        <div className="flex justify-start items-center">

          <button

            type="button"

            onClick={() =>
              setResponseView("responses")
            }

            className={`rounded px-4 py-2 text-[10px] font-bold mr-1 ${
              responseView === "responses"
                ? "bg-orange-600 text-white"
                : "bg-white text-gray-600"
            }`}

          >

            Candidate Responses

          </button>


          <button

            type="button"

            onClick={() =>
              setResponseView("insights")
            }

            className={`rounded px-4 py-2 text-[10px] font-bold ${
              responseView === "insights"
                ? "bg-orange-600 text-white"
                : "bg-white text-gray-600"
            }`}

          >

            Response Insights

          </button>

        </div>

      </div>


      {/* Summary Error */}

      {isSummaryError && (

        <div className="bg-orange-50 rounded m-1 p-3">

          <p className="text-xs text-orange-700">

            {
              summaryError?.data?.message ||
              "Unable to load Intention Board response summary."
            }

          </p>

        </div>

      )}


      {/* Refresh */}

      {isFetchingSummary &&
        !isLoadingSummary && (

        <div className="m-2">

          <PulseLoader
            color={"#ea580c"}
            size={5}
          />

        </div>

      )}


      {/*
      |--------------------------------------------------------------------------
      | Candidate Responses
      |--------------------------------------------------------------------------
      */}

      {responseView === "responses" && (

        <>

          {/*
          |--------------------------------------------------------------------------
          | Summary Cards
          |--------------------------------------------------------------------------
          */}

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-1 m-1">

            {/* Candidates */}

            <div className="bg-gray-100 rounded p-3">

              <p className="text-[9px] font-bold text-gray-500 uppercase">
                Candidates
              </p>

              <p className="text-xl font-bold text-gray-800 mt-1">

                {summary?.totalCandidates || 0}

              </p>

            </div>


            {/* Not Started */}

            <div className="bg-gray-100 rounded p-3">

              <p className="text-[9px] font-bold text-gray-500 uppercase">
                Not Started
              </p>

              <p className="text-xl font-bold text-gray-800 mt-1">

                {summary?.notStarted || 0}

              </p>

            </div>


            {/* In Progress */}

            <div className="bg-yellow-50 rounded p-3">

              <p className="text-[9px] font-bold text-yellow-700 uppercase">
                In Progress
              </p>

              <p className="text-xl font-bold text-gray-800 mt-1">

                {summary?.inProgress || 0}

              </p>

            </div>


            {/* Submitted */}

            <div className="bg-lime-50 rounded p-3">

              <p className="text-[9px] font-bold text-lime-700 uppercase">
                Submitted
              </p>

              <p className="text-xl font-bold text-gray-800 mt-1">

                {summary?.submitted || 0}

              </p>

            </div>


            {/* Overdue */}

            <div className="bg-orange-50 rounded p-3">

              <p className="text-[9px] font-bold text-orange-700 uppercase">
                Overdue
              </p>

              <p className="text-xl font-bold text-gray-800 mt-1">

                {summary?.overdue || 0}

              </p>

            </div>


            {/*
            |--------------------------------------------------------------------------
            | Submission Percentage Donut
            |--------------------------------------------------------------------------
            */}

            <div className="bg-orange-50 rounded p-3">

              <div className="flex justify-between items-center">

                <div>

                  <p className="text-[9px] font-bold text-orange-700 uppercase">
                    Submission %
                  </p>

                  <p className="text-[9px] text-gray-500 mt-1">

                    {submittedCandidates}
                    {" / "}
                    {totalCandidates}

                  </p>

                </div>


                <div className="relative w-[76px] h-[76px]">

                  <svg
                    viewBox="0 0 100 100"
                    className="w-full h-full -rotate-90"
                  >

                    {/* Background Ring */}

                    <circle
                      cx="50"
                      cy="50"
                      r={donutRadius}
                      fill="none"
                      stroke="#fed7aa"
                      strokeWidth="10"
                    />


                    {/* Submission Ring */}

                    <circle
                      cx="50"
                      cy="50"
                      r={donutRadius}
                      fill="none"
                      stroke="#ea580c"
                      strokeWidth="10"
                      strokeLinecap="round"
                      strokeDasharray={
                        `${donutProgress} ${donutRemaining}`
                      }
                    />

                  </svg>


                  <div className="absolute inset-0 flex justify-center items-center">

                    <p className="text-sm font-bold text-orange-700">

                      {submissionPercentage}%

                    </p>

                  </div>

                </div>

              </div>

            </div>

          </div>


          {/*
          |--------------------------------------------------------------------------
          | Main Layout
          |--------------------------------------------------------------------------
          */}

          <div
            className="
              grid
              grid-cols-1
              lg:grid-cols-12
              gap-1
              m-1
              lg:h-[70vh]
              lg:overflow-hidden
            "
          >

            {/*
            |--------------------------------------------------------------------------
            | LEFT - Candidates
            |--------------------------------------------------------------------------
            */}

            <div
              className="
                lg:col-span-4
                bg-gray-200
                rounded
                p-1
                lg:h-full
                lg:min-h-0
                lg:flex
                lg:flex-col
                lg:overflow-hidden
              "
            >

              {/*
              |--------------------------------------------------------------------------
              | Candidate Header and Search - 29 Sep 2026
              |--------------------------------------------------------------------------
              */}

              <div className="bg-white rounded m-1 p-3 shrink-0">

                <p className="font-bold text-xs text-gray-800">
                  Candidates
                </p>

                <p className="text-[9px] text-gray-500 mt-1">
                  Submitted candidates can be opened to review their responses.
                </p>


                {/*
                |--------------------------------------------------------------------------
                | Search Input
                |--------------------------------------------------------------------------
                */}

                <div className="relative mt-3">

                  <MagnifyingGlassIcon
                    className="
                      absolute
                      left-3
                      top-1/2
                      -translate-y-1/2
                      h-4
                      w-4
                      text-gray-400
                      pointer-events-none
                    "
                  />


                  <input

                    type="text"

                    value={
                      candidateSearch
                    }

                    onChange={
                      event =>
                        setCandidateSearch(
                          event.target.value
                        )
                    }

                    placeholder="Search by name or email..."

                    aria-label="Search candidates by name or email"

                    className="
                      w-full
                      rounded
                      border
                      border-gray-200
                      bg-gray-50
                      pl-9
                      pr-8
                      py-2
                      text-[11px]
                      text-gray-700
                      outline-none
                      focus:border-orange-400
                    "

                  />


                  {candidateSearch && (

                    <button

                      type="button"

                      title="Clear search"

                      aria-label="Clear candidate search"

                      onClick={() =>
                        setCandidateSearch("")
                      }

                      className="
                        absolute
                        right-2
                        top-1/2
                        -translate-y-1/2
                        text-gray-400
                        hover:text-orange-600
                      "

                    >

                      <XMarkIcon
                        className="h-4 w-4"
                      />

                    </button>

                  )}

                </div>


                {/*
                |--------------------------------------------------------------------------
                | Visible Candidate Count
                |--------------------------------------------------------------------------
                */}

                <div className="flex justify-between items-center gap-2 mt-3">

                  <p className="text-[9px] text-gray-500">

                    {
                      normalizedCandidateSearch
                        ? "Search Results"
                        : "All Candidates"
                    }

                  </p>


                  <p className="text-[9px] font-bold text-gray-600">

                    {filteredCandidates.length}
                    {" of "}
                    {sortedCandidates.length}

                  </p>

                </div>

              </div>


              {/*
              |--------------------------------------------------------------------------
              | No Candidates
              |--------------------------------------------------------------------------
              */}

              {candidates.length === 0 && (

                <div className="bg-white rounded m-1 p-3">

                  <p className="text-xs text-gray-500">
                    No candidates have been added to this Intention Board.
                  </p>

                </div>

              )}


              {/*
              |--------------------------------------------------------------------------
              | Candidate List
              |--------------------------------------------------------------------------
              */}

              <div
                className="
                  lg:flex-1
                  lg:min-h-0
                  lg:overflow-y-auto
                "
              >

                {/*
                |--------------------------------------------------------------------------
                | No Search Matches
                |--------------------------------------------------------------------------
                */}

                {sortedCandidates.length > 0
                  &&
                  filteredCandidates.length === 0
                  &&
                  (

                    <div className="bg-white rounded m-1 p-4">

                      <p className="text-xs font-bold text-gray-700">
                        No matching candidates
                      </p>

                      <p className="text-[10px] text-gray-500 mt-1">
                        Try another name or email address.
                      </p>


                      <button

                        type="button"

                        onClick={() =>
                          setCandidateSearch("")
                        }

                        className="text-[10px] font-bold text-orange-600 mt-3"

                      >

                        Clear Search

                      </button>

                    </div>

                  )
                }


                {/*
                |--------------------------------------------------------------------------
                | Filtered and Sorted Candidate Rows
                |--------------------------------------------------------------------------
                */}

                {filteredCandidates.map(
                  (candidate, index) => {

                    const canOpen =
                      candidate?.hasSubmittedResponse === true


                    const isSelected =
                      selectedCandidateId ===
                      candidate?.intentionCandidateId


                    return (

                      <div

                        key={
                          candidate?.intentionCandidateId ||
                          index
                        }

                        onClick={() =>
                          handleSelectCandidate(
                            candidate
                          )
                        }

                        className={`rounded m-1 p-3 ${
                          isSelected
                            ? "bg-orange-50"
                            : "bg-white"
                        } ${
                          canOpen
                            ? "cursor-pointer hover:bg-orange-50"
                            : "cursor-default"
                        }`}

                      >

                        <div className="flex justify-between items-start gap-2">

                          <div className="flex justify-start items-start gap-2 flex-1 min-w-0">

                            <div className="w-6 h-6 rounded-full bg-gray-100 flex justify-center items-center shrink-0">

                              <p className="text-[9px] font-bold text-gray-600">

                                {index + 1}

                              </p>

                            </div>


                            <div className="flex-1 min-w-0">

                              <p

                                className="
                                  text-xs
                                  font-bold
                                  text-gray-800
                                  leading-snug
                                  break-words
                                  line-clamp-2
                                "

                                title={
                                  candidate?.candidateName
                                }

                              >

                                {candidate?.candidateName}

                              </p>


                              <p

                                className="
                                  text-[9px]
                                  text-gray-500
                                  mt-1
                                  truncate
                                "

                                title={
                                  candidate?.candidateEmail
                                }

                              >

                                {candidate?.candidateEmail}

                              </p>

                            </div>

                          </div>


                          <p
                            className={`text-[9px] font-bold rounded-full px-2 py-1 shrink-0 whitespace-nowrap ${
                              getCandidateStatusClass(
                                candidate
                              )
                            }`}
                          >

                            {
                              getCandidateStatusLabel(
                                candidate
                              )
                            }

                          </p>

                        </div>


                        {candidate?.submittedAt && (

                          <p className="text-[9px] text-gray-500 mt-2">

                            Submitted: {
                              formatDateTime(
                                candidate.submittedAt
                              )
                            }

                          </p>

                        )}


                        {candidate?.submissionMode && (

                          <p className="text-[9px] text-gray-500 mt-1">

                            {
                              candidate.submissionMode ===
                              "automatic_deadline"
                                ? "Automatic deadline submission"
                                : "Manual submission"
                            }

                          </p>

                        )}

                      </div>

                    )

                  }
                )}

              </div>

            </div>


            {/*
            |--------------------------------------------------------------------------
            | RIGHT - Response Detail
            |--------------------------------------------------------------------------
            */}

            <div

              ref={
                responseDetailPaneRef
              }

              className="
                lg:col-span-8
                bg-gray-200
                rounded
                p-1
                lg:h-full
                lg:min-h-0
                lg:overflow-y-auto
              "

            >

              {!selectedCandidateId && (

                <div className="bg-white rounded m-1 p-6">

                  <p className="text-sm font-bold text-gray-700">
                    Submitted Response
                  </p>

                  <p className="text-xs text-gray-500 mt-2">
                    Select a submitted candidate to view their Intention Board response.
                  </p>

                </div>

              )}


              {selectedCandidateId &&
                isFetchingCandidateResponse && (

                <div className="bg-white rounded m-1 p-6">

                  <PulseLoader
                    color={"#ea580c"}
                    size={7}
                  />

                </div>

              )}


              {selectedCandidateId &&
                isCandidateResponseError && (

                <div className="bg-orange-50 rounded m-1 p-4">

                  <p className="text-xs text-orange-700">

                    {
                      candidateResponseError?.data?.message ||
                      "Unable to load candidate response."
                    }

                  </p>

                </div>

              )}


              {selectedCandidateId &&
                !isFetchingCandidateResponse &&
                selectedCandidate && (

                <div>

                  {/*
                  |--------------------------------------------------------------------------
                  | Candidate Header
                  |--------------------------------------------------------------------------
                  */}

                  <div className="bg-white rounded m-1 p-4">

                    <div className="flex justify-between items-start">

                      <div>

                        <p className="text-lg font-bold text-gray-800">

                          {selectedCandidate?.candidateName}

                        </p>


                        <p className="text-xs text-gray-500 mt-1">

                          {selectedCandidate?.candidateEmail}

                        </p>

                      </div>


                      <button

                        type="button"

                        onClick={
                          handleCloseCandidate
                        }

                        className="text-[10px] font-bold text-orange-600"

                      >

                        Close

                      </button>

                    </div>


                    <div className="flex justify-start items-center flex-wrap mt-3">

                      <p className="text-[9px] font-bold bg-lime-100 text-lime-700 rounded-full px-2 py-1 mr-2">
                        Submitted
                      </p>


                      {selectedResponse?.submissionMode && (

                        <p className="text-[9px] font-bold bg-gray-100 text-gray-600 rounded-full px-2 py-1 mr-2">

                          {
                            selectedResponse.submissionMode ===
                            "automatic_deadline"
                              ? "Automatic Deadline"
                              : "Manual"
                          }

                        </p>

                      )}


                      {selectedResponse?.submittedAt && (

                        <p className="text-[9px] text-gray-500">

                          {
                            formatDateTime(
                              selectedResponse.submittedAt
                            )
                          }

                        </p>

                      )}

                    </div>

                  </div>


                  {/*
                  |--------------------------------------------------------------------------
                  | Questions / Responses
                  |--------------------------------------------------------------------------
                  */}

                  {responseQuestions.map(
                    (question, index) => (

                      <div

                        key={
                          question?.intentionQuestionId ||
                          index
                        }

                        className="bg-white rounded m-1 p-4"

                      >

                        <div className="flex justify-start items-start">

                          <p className="text-xs font-bold text-orange-600 mr-2">

                            {
                              question?.questionNumber ||
                              index + 1
                            }.

                          </p>


                          <div className="flex-1">

                            <p className="text-xs font-bold text-gray-800">

                              {question?.questionText}

                            </p>


                            {question?.questionDescription && (

                              <p className="text-[10px] text-gray-500 mt-1">

                                {question.questionDescription}

                              </p>

                            )}

                          </div>

                        </div>


                        <div className="bg-gray-50 rounded mt-3 p-3">

                          {
                            renderAnswer(
                              question
                            )
                          }

                        </div>

                      </div>

                    )
                  )}

                </div>

              )}

            </div>

          </div>

        </>

      )}


      {/*
      |--------------------------------------------------------------------------
      | Response Insights - 13 Sep 2026
      |--------------------------------------------------------------------------
      */}

      {responseView === "insights" && (

        <EtOrgIntentionResponseInsights

          intentionBoardId={
            intentionBoardId
          }

        />

      )}

    </div>

  )

}


export default EtOrgIntentionResponseBoard